import { ChangeDetectionStrategy, Component, computed, effect } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { ExamdojoAgGridModule } from '@examdojo/ag-grid';
import { ComponentSize } from '@examdojo/core/model';
import { SelectableTreeBaseComponent, TreeNode } from '@examdojo/core/tree';
import { SearchInputComponent } from '@examdojo/ui/input';
import { PlatformService } from '@examdojo/platform';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { GridReadyEvent } from 'ag-grid-community';
import { QuestionBankTopicsTreeCellRendererComponent } from './cell-renderer/question-bank-topics-tree-cell-renderer.component';

export type QuestionBankTopicTreeNode = TreeNode<{ id: number; code: string; sort_order: number }, { count?: number }>;

@Component({
  selector: 'dojo-question-bank-topics-tree',
  templateUrl: './question-bank-topics-tree.component.html',
  styleUrls: ['./question-bank-topics-tree.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [ExamdojoAgGridModule, SearchInputComponent, TranslocoPipe],
})
export class QuestionBankTopicsTreeComponent extends SelectableTreeBaseComponent<
  NonNullable<QuestionBankTopicTreeNode['originalData']>,
  NonNullable<QuestionBankTopicTreeNode['metadata']>
> {
  constructor(
    private readonly platformService: PlatformService,
    private readonly translocoService: TranslocoService,
  ) {
    super({
      domLayout: platformService.isSmall() ? 'autoHeight' : 'normal',
      suppressContextMenu: true,
      overlayNoRowsTemplate: translocoService.translate('examdojo.topics_tree.no_topics_to_display'),
      initialGroupOrderComparator: (params) => {
        const orderA = params.nodeA.data?.originalData?.sort_order ?? 0;
        const orderB = params.nodeB.data?.originalData?.sort_order ?? 0;
        return orderA - orderB;
      },
      autoGroupColumnDef: {
        cellRenderer: QuestionBankTopicsTreeCellRendererComponent,
        getQuickFilterText: (params) => {
          const code = params.data?.originalData?.code || '';
          const name = params.data?.name || '';
          return `${code} ${name}`.trim();
        },
      },
    });

    effect(() => {
      this.updateDomLayout();
    });
  }

  readonly searchCtrl = new FormControl<string>('', { nonNullable: true });
  readonly searchSize = computed<ComponentSize>(() => (this.isSmall() ? 's' : 'xs'));

  private readonly isSmall = toSignal(this.platformService.isSmall$);

  override onGridReady(params: GridReadyEvent<QuestionBankTopicTreeNode>): void {
    super.onGridReady(params);
    this.updateDomLayout();
  }

  onSearch(value: string): void {
    if (this.gridApi) {
      this.gridApi.setGridOption('quickFilterText', value);
    }
  }

  private updateDomLayout(): void {
    if (!this.gridApi) {
      return;
    }

    const isSmall = this.isSmall();
    this.gridApi.setGridOption('domLayout', isSmall ? 'autoHeight' : 'normal');
  }
}
